import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWebSocket } from './WebSocketContext';
import { useSnackbar } from '../Utils/SnackBarContext';

/**
 * Generic storage upload helper for uploading files via websocket and receiving a response.
 * Keeps upload UX/notifications consistent and reusable.
 */
export function useStorageUpload({
  command = 'uploadTemplateFile',
  caseid,
  accept = '',
  successMessage = 'Upload completed',
  errorMessage = 'Upload failed',
  sendingMessage = 'Uploading...',
  onSuccess,
  onError,
  meta
}) {
  const { sendMessage, subscribeToMessages, ws } = useWebSocket();
  const { openSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputRef = useRef(null);
  const pendingFilesRef = useRef(null);
  const pendingTimerRef = useRef(null);
  const isSocketOpen = ws && ws.readyState === WebSocket.OPEN;
  const CONNECTION_WAIT_MS = 2000;
  const normalizedMeta = useMemo(() => {
    if (meta && typeof meta === 'object') {
      return meta;
    }
    return {};
  }, [meta]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages([command], (message) => {
      const { status, message: serverMessage } = message.data || {};
      if (status === 'success') {
        openSnackbar(serverMessage || successMessage, 4000);
        if (onSuccess) onSuccess(message.data);
      } else {
        openSnackbar(serverMessage || errorMessage, 4000);
        if (onError) onError(message.data);
      }
      setUploading(false);
      setUploadProgress(0);
    });
    return unsubscribe;
  }, [command, subscribeToMessages, openSnackbar, successMessage, errorMessage, onSuccess, onError]);

  const triggerFileDialog = useCallback(() => {
    if (uploading) return;
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [uploading]);

  const readFileAsDataUrl = useCallback((file, offset = 0, totalBytes = 1) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          const safeTotal = Math.max(1, totalBytes);
          const safeLoaded = Math.min(event.loaded || 0, file?.size || event.total || safeTotal);
          const globalLoaded = Math.min(safeTotal, (offset || 0) + safeLoaded);
          const percent = Math.round((globalLoaded / safeTotal) * 100);
          setUploadProgress((prev) => Math.min(100, Math.max(prev, percent)));
        }
      };
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const startUpload = useCallback(async (files) => {
    const fileList = Array.isArray(files) ? files : [files];
    if (!fileList.length || !sendMessage) {
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    const offsets = [];
    let accumulated = 0;
    fileList.forEach((file) => {
      offsets.push(accumulated);
      const size = typeof file?.size === 'number' ? file.size : 0;
      accumulated += size;
    });
    const totalBytes = Math.max(accumulated, fileList.length);
    try {
      const filePayloads = await Promise.all(fileList.map(async (file, index) => ({
        filename: file.name,
        filedata: await readFileAsDataUrl(file, offsets[index], totalBytes)
      })));

      setUploadProgress(100);

      const payload = {
        command,
        caseid,
        files: filePayloads,
        ...normalizedMeta
      };

      sendMessage(payload);
      openSnackbar(sendingMessage, 10000);
    } catch (error) {
      console.error(`Failed to send ${command}`, error);
      openSnackbar(errorMessage, 4000);
      setUploading(false);
      setUploadProgress(0);
    }
  }, [caseid, command, errorMessage, normalizedMeta, openSnackbar, readFileAsDataUrl, sendMessage, sendingMessage]);

  useEffect(() => {
    if (!isSocketOpen || !pendingFilesRef.current) return;

    const pendingFiles = pendingFilesRef.current;
    pendingFilesRef.current = null;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    startUpload(pendingFiles);
  }, [isSocketOpen, startUpload]);

  const handleFiles = useCallback((incoming) => {
    const fileList = Array.isArray(incoming)
      ? incoming
      : Array.from(incoming || []);
    if (!fileList.length) return;
    setUploadProgress(0);

    if (accept) {
      const allowedExts = accept.split(',').map(ext => ext.trim().toLowerCase()).filter(Boolean);
      const invalidFile = fileList.find(f => !allowedExts.some(ext => f.name.toLowerCase().endsWith(ext)));
      if (invalidFile) {
        openSnackbar(errorMessage, 4000);
        return;
      }
    }

    if (!isSocketOpen) {
      pendingFilesRef.current = fileList;
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
      openSnackbar('Waiting for server connection...', 4000);
      pendingTimerRef.current = setTimeout(() => {
        pendingFilesRef.current = null;
        pendingTimerRef.current = null;
        openSnackbar(errorMessage, 4000);
      }, CONNECTION_WAIT_MS);
      return;
    }

    startUpload(fileList);
  }, [accept, errorMessage, isSocketOpen, openSnackbar, startUpload, CONNECTION_WAIT_MS]);

  const handleFileChange = useCallback((event) => {
    const files = event.target?.files ? Array.from(event.target.files) : [];
    if (event.target) {
      event.target.value = '';
    }
    handleFiles(files);
  }, [handleFiles]);

  return {
    uploading,
    uploadProgress,
    inputRef,
    handleFiles,
    handleFileChange,
    triggerFileDialog
  };
}

export default useStorageUpload;
